import type { PushDeviceInput } from "@estelle/domain";

/**
 * The browser's half of push (E6, #446, slice 5): can this browser do it, is
 * this device subscribed, and turning it on and off.
 *
 * Nothing here talks to the API; the card does that, with what these return.
 * Nothing here runs until a person clicks: the browser's permission prompt is
 * only ever asked for from `subscribeThisDevice`, never on load.
 */

/**
 * - `supported`    push can be turned on here.
 * - `needs-install` iPhone and iPad only deliver push to an app added to the Home
 *   Screen; in an ordinary Safari tab the push API is not there at all.
 * - `unsupported`  this browser cannot do push.
 */
export type PushSupport = "supported" | "needs-install" | "unsupported";

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function pushSupport(): PushSupport {
  if (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  ) {
    return "supported";
  }
  const apple = /iPhone|iPad|iPod/.test(navigator.userAgent);
  return apple && !isStandalone() ? "needs-install" : "unsupported";
}

export function notificationPermission(): NotificationPermission {
  return "Notification" in window ? Notification.permission : "denied";
}

/** The worker, registered if it is not yet — idempotent, and the same file `register.ts` registers. */
async function ensureRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) return existing;
  await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  return navigator.serviceWorker.ready;
}

/** This browser's push subscription, or null. Never prompts. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== "supported") return null;
  const registration = await navigator.serviceWorker.getRegistration("/");
  return registration ? registration.pushManager.getSubscription() : null;
}

/**
 * The first sixteen hex digits of the SHA-256 of a push address — the same
 * fingerprint the API gives each device (`deviceKey` in `push/devices.ts`), so
 * this browser can pick its own entry out of the list without the address ever
 * being returned.
 */
export async function deviceKey(endpoint: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(endpoint),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  )
    .join("")
    .slice(0, 16);
}

/** A base64url string as the bytes `subscribe` wants. */
export function applicationServerKey(publicKey: string): Uint8Array {
  const padded = publicKey + "=".repeat((4 - (publicKey.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

/** What the API stores of a subscription. Null if the browser handed back less than that. */
export function toDeviceInput(
  subscription: PushSubscription,
): PushDeviceInput | null {
  const json = subscription.toJSON();
  const keys = json.keys;
  if (!json.endpoint || !keys?.p256dh || !keys.auth) return null;
  return {
    endpoint: json.endpoint,
    keys: { p256dh: keys.p256dh, auth: keys.auth },
  };
}

/**
 * Asks the browser's permission and subscribes — call it from a click and
 * nowhere else. Resolves to the subscription, or throws with a message a person
 * can read: permission refused, or the push service unreachable.
 */
export async function subscribeThisDevice(
  publicKey: string,
): Promise<PushSubscription> {
  if (pushSupport() !== "supported") {
    throw new Error("This browser cannot receive push notifications.");
  }
  const permission =
    Notification.permission === "default"
      ? await Notification.requestPermission()
      : Notification.permission;
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked for this site. Allow them in the browser's site settings, then try again."
        : "Notifications were not allowed.",
    );
  }

  const registration = await ensureRegistration();
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;
  try {
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(publicKey) as BufferSource,
    });
  } catch {
    throw new Error(
      "Could not reach the browser's push service. Check the connection and try again.",
    );
  }
}

/** Unsubscribes this browser; returns the address it had, or null if there was none. */
export async function unsubscribeThisDevice(): Promise<string | null> {
  const subscription = await currentSubscription();
  if (!subscription) return null;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => false);
  return endpoint;
}
